import { useEffect, useState } from 'react'
import { Trash2, Truck, CalendarDays, ExternalLink } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useBackupDaysStore } from '@/stores/useBackupDaysStore'
import { useCalendarLookupStore } from '@/stores/useCalendarLookupStore'
import {
  CALENDAR_COLUMNS,
  dynamicForRuleEdit,
  VALUE_CHOOSER_MODES,
  type CalendarColumn,
  type Labeled,
  type ValueChooserMode,
  type ValueChooserRule,
} from './clt.mock'

interface ValueChooserPopoverProps {
  value: ValueChooserRule | null
  onApply: (rule: ValueChooserRule | null) => void
  /** Milestone list from the API — surfaced as the "Calculate from" options. */
  milestones: Labeled[]
  /** Hide this milestone from the "calculate from" list (no self-reference). */
  excludeMilestoneId?: string
  title?: string
  /** Show a Clear button (only meaningful when editing an existing cell). */
  allowClear?: boolean
  align?: 'start' | 'center' | 'end'
  children: React.ReactNode
}

export function ValueChooserPopover({
  value,
  onApply,
  milestones,
  excludeMilestoneId,
  title = 'Value Chooser',
  allowClear = false,
  align = 'start',
  children,
}: ValueChooserPopoverProps) {
  const [open, setOpen] = useState(false)
  const backupDaysOpen = useBackupDaysStore(s => s.isOpen)
  const openBackupDays = useBackupDaysStore(s => s.open)
  const calendarLookupOpen = useCalendarLookupStore(s => s.isOpen)
  const openCalendarLookup = useCalendarLookupStore(s => s.open)
  const [mode, setMode] = useState<ValueChooserMode>('DELTA')
  const [fromMilestoneId, setFromMilestoneId] = useState<string>('')
  const [deltaDays, setDeltaDays] = useState<string>('')
  const [calendarColumn, setCalendarColumn] = useState<CalendarColumn | ''>('')

  // Re-seed the draft from the current value each time the popover opens.
  useEffect(() => {
    if (!open) return
    setMode(value?.mode ?? 'DELTA')
    setFromMilestoneId(value?.fromMilestoneId ?? '')
    setDeltaDays(value?.deltaDays === undefined ? '' : String(value.deltaDays))
    setCalendarColumn(value?.calendarColumn ?? '')
  }, [open, value])

  const milestoneOptions = milestones.filter(m => m.id !== excludeMilestoneId)

  const deltaIsValid =
    deltaDays.trim() !== '' && Number.isFinite(Number(deltaDays))
  const isValid =
    mode === 'DELTA'
      ? Boolean(fromMilestoneId) && deltaIsValid
      : mode === 'BACKUP_DAYS'
        ? Boolean(fromMilestoneId)
        : Boolean(calendarColumn)

  const handleApply = () => {
    if (!isValid) return
    const dynamic = dynamicForRuleEdit(mode, value)
    const rule: ValueChooserRule =
      mode === 'DELTA'
        ? { mode, dynamic, fromMilestoneId, deltaDays: Number(deltaDays) }
        : mode === 'BACKUP_DAYS'
          ? {
              mode,
              dynamic,
              fromMilestoneId,
              ...(deltaIsValid ? { deltaDays: Number(deltaDays) } : {}),
            }
          : { mode, dynamic, calendarColumn: calendarColumn as CalendarColumn }
    onApply(rule)
    setOpen(false)
  }

  const handleClear = () => {
    onApply(null)
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        // Don't let the popover close while a reference-data dialog (backup
        // days / calendar lookup) is open on top of it.
        if (!next && (backupDaysOpen || calendarLookupOpen)) return
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-[380px] p-0">
        <div className="border-b border-border px-4 py-3">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Choose how this date is calculated.
          </p>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-4 py-3">
          <RadioGroup
            value={mode}
            onValueChange={v => {
              // Reset mode-specific fields so the user consciously re-chooses.
              setMode(v as ValueChooserMode)
              setFromMilestoneId('')
              setDeltaDays('')
              setCalendarColumn('')
            }}
            className="gap-2"
          >
            {VALUE_CHOOSER_MODES.map(option => (
              <label
                key={option.value}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2.5 text-sm transition-colors hover:bg-muted/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              >
                <RadioGroupItem value={option.value} className="mt-0.5" />
                <span className="font-medium text-foreground">
                  {option.label}
                </span>
              </label>
            ))}
          </RadioGroup>

          {/* Mode-specific fields */}
          <div className="mt-4 space-y-3">
            {mode === 'DELTA' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Delta (days)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. -14"
                    value={deltaDays}
                    onChange={e => setDeltaDays(e.target.value)}
                    className="h-9"
                  />
                  <p className="text-xs text-muted-foreground">
                    Negative = before, positive = after the reference milestone.
                  </p>
                </div>
                <MilestonePicker
                  label="Calculate from milestone"
                  value={fromMilestoneId}
                  onChange={setFromMilestoneId}
                  options={milestoneOptions}
                />
              </>
            )}

            {mode === 'BACKUP_DAYS' && (
              <>
                <MilestonePicker
                  label="Calculate from milestone"
                  value={fromMilestoneId}
                  onChange={setFromMilestoneId}
                  options={milestoneOptions}
                />
                <div className="space-y-1.5">
                  <Label className="text-xs">Additional days (optional)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. +8"
                    value={deltaDays}
                    onChange={e => setDeltaDays(e.target.value)}
                    className="h-9"
                  />
                  <p className="text-xs text-muted-foreground">
                    Added on top of the looked-up backup days.
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  The transit days come from the preloaded matrix and vary per
                  origin and warehouse (DC), per leg (Shipping / Rail / Truck).
                </p>
                <button
                  type="button"
                  onClick={openBackupDays}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                    View / manage backup days matrix
                  </span>
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </>
            )}

            {mode === 'CALENDAR_LOOKUP' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Calendar date</Label>
                  <Select
                    value={calendarColumn}
                    onValueChange={v => setCalendarColumn(v as CalendarColumn)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Pick a calendar date" />
                    </SelectTrigger>
                    <SelectContent>
                      {CALENDAR_COLUMNS.map(col => (
                        <SelectItem key={col.value} value={col.value}>
                          {col.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  At instantiation, this date is read per event &amp; DC from
                  the calendar lookup table.
                </p>
                <button
                  type="button"
                  onClick={openCalendarLookup}
                  className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <span className="flex items-center gap-2">
                    <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                    View calendar lookup table
                  </span>
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
          {allowClear ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={handleClear}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleApply} disabled={!isValid}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function MilestonePicker({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { id: string; label: string }[]
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9">
          <SelectValue placeholder="Select milestone" />
        </SelectTrigger>
        <SelectContent>
          {options.map(milestone => (
            <SelectItem key={milestone.id} value={milestone.id}>
              {milestone.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
